/** One filing: its readiness, its path and the list of what is left - ADR-0212 decision 6, as
 * reordered by the 2026-11-03 design review.
 *
 * Top to bottom: the header (title, Preview, Send for approval, More); the **hero** - the percentage
 * beside the plain verdict word and the support chip, the filing's date and counts, and the **path to
 * filing** with the one item in its way; **the to-do list directly under it** - the failing and unrun
 * checks, each with the fix that closes it, the passed ones behind "Show passed"; then **what the
 * filing states** (the report's figure lines, each opening its receipt), one collapsed row for the
 * regime's dates and first-report rules, and the filing's longer views.
 *
 * **Nothing is computed here.** The percentage, the counts, the verdict, the support level and the
 * in-force caveat are the server's (`GET .../checks`); the list is that same response, filtered; the
 * figures are the report's own lines. A filing is addressed by its obligation id (ADR-0029). The page
 * says in one place what the percentage is and is not (behind its info control): it is not the
 * verdict, and nothing on this page means a filing was sent to a regulator.
 *
 * **What was removed, and why it is not missed.** A linear bar, a ring and a "Checks by station" funnel
 * each drew the same fact the percentage and the path already state (the page said "77%" five times),
 * and two tiles repeated the counts. They sat above the to-do list and pushed it to the bottom of a
 * 900 px screen.
 */

import { Flag } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { ReactNode } from "react";

import { useMe } from "../../api/auth";
import { maySubmit } from "../../api/approvals";
import { useFilingChecks } from "../../api/filingChecks";
import type { Obligation, Scope2Method } from "../../api/queries";
import { useArtifacts, useReport } from "../../api/queries";
import { useRecordWork } from "../../api/work";
import { ReadFailed } from "../../components/Loaded";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterState } from "../../components/ui/filters";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { PathToFiling } from "../../components/ui/PathToFiling";
import { SectionTabs } from "../../components/ui/SectionTabs";
import { StatusChip } from "../../components/ui/StatusChip";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { customerText, supportLevelWords } from "../../lib/customerWords";
import { shortDate } from "../../lib/format";
import { wordOf } from "../filingChecksModel";
import { stepsFromChecks } from "../filingSpine";
import type { SpineStep } from "../filingSpine";
import { MAY_DRAFT } from "../mayDraft";
import { isoToday } from "../readinessModel";
import { dueOf } from "../reportsModel";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import { FilingActions } from "./FilingActions";
import { FilingFacts } from "./FilingFacts";
import styles from "./Filings.module.css";
import { isOpenRequest, useReviewerWords } from "./approvalRequest";
import { SendForApprovalDrawer } from "./SendForApproval";
import { ServerNote } from "./ServerNote";
import { StatedBand } from "./StatedBand";
import { blockerText, buildTodo, needsRoles, withOpenRequest } from "./todo";
import type { TodoRow } from "./todo";
import { headlineWord, percentText } from "./words";

/** The views of a filing that carry the long text, in the order the work runs. */
const DETAIL_VIEWS = [
  { tab: "disclosures", label: "Disclosures" },
  { tab: "findings", label: "Findings" },
  { tab: "bridge", label: "Year on year" },
  { tab: "issue", label: "Issue" },
  { tab: "submission", label: "Submission pack" },
] as const;

function FixCell({
  row,
  filing,
  onSend,
}: {
  row: TodoRow;
  filing: string;
  onSend: () => void;
}) {
  const fix = row.fix;
  if (!fix) return <span className={styles.refused}>–</span>;
  if (!fix.permitted) return <span className={styles.refused}>{needsRoles(fix.roles)}</span>;
  // Asking for approval is a drawer on this filing: a button, not a trip to another page.
  if (fix.opens === "send_for_approval") {
    return (
      <button type="button" className={styles.fixButton} onClick={onSend} data-testid="todo-fix">
        {fix.label}
      </button>
    );
  }
  return (
    <Link
      to={fix.to}
      search={(prev: Partial<ScreenSearch>) => ({
        ...keepSearch(prev),
        ...(fix.to === "/reports" ? { filing, tab: fix.tab } : fix.tab ? { tab: fix.tab } : {}),
      })}
      className={styles.fix}
      data-testid="todo-fix"
    >
      {fix.label}
    </Link>
  );
}

function columnsFor(filing: string, onSend: () => void): GridColumn<TodoRow>[] {
  return [
    {
      id: "what",
      header: "To do",
      sortable: false,
      cell: (row) => (
        <span className={styles.todoWhat}>
          <span>{row.what}</span>
          {row.reason ? (
            <span className={styles.todoReason} data-testid="todo-reason">
              {row.reason}
            </span>
          ) : null}
        </span>
      ),
    },
    { id: "site", header: "Site", cell: (row) => row.site || "–" },
    { id: "period", header: "Period", cell: (row) => row.period || "–" },
    { id: "station", header: "Station", cell: (row) => row.station },
    {
      id: "state",
      header: "State",
      cell: (row) => {
        const { word, tone } = wordOf(row.state);
        return (
          <StatusChip tone={tone} size="sm">
            {word}
          </StatusChip>
        );
      },
    },
    { id: "fix", header: "Fix", cell: (row) => <FixCell row={row} filing={filing} onSend={onSend} /> },
  ];
}

function matches(row: TodoRow, filters: FilterState): boolean {
  const needle = filters.search.trim().toLowerCase();
  if (needle && !`${row.what} ${row.reason} ${row.site} ${row.period} ${row.station}`.toLowerCase().includes(needle)) {
    return false;
  }
  const stations = filters.values.station ?? [];
  if (stations.length > 0 && !stations.includes(row.stationKey)) return false;
  return true;
}

/** Once an approval request is open the Approvals station says who has it. Only the note moves (and
 * the station is the current one when it is where the filing is): a request is not an approval, and
 * nothing here ticks a station the server did not tick. */
function withReviewer(steps: readonly SpineStep[], who: string | undefined): SpineStep[] {
  if (!who) return [...steps];
  return steps.map((step) =>
    step.key === "approvals" && step.state !== "done"
      ? { ...step, note: `With ${who}`, ...(step.here ? { state: "current" as const } : {}) }
      : step,
  );
}

export function FilingPage({
  active,
  method,
  title,
  lead,
}: {
  active: Obligation;
  method: Scope2Method;
  /** The place's name for this filing: the regime and the entity. */
  title: string;
  /** Drawn above the readiness card: the place's regime tabs. */
  lead?: ReactNode;
}) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const checks = useFilingChecks(active.obligation_id, method);
  const report = useReport(active.obligation_id, method);
  const artifacts = useArtifacts(active.obligation_id);
  const work = useRecordWork(me && maySubmit(me.role) ? active.obligation_id : undefined);
  const [showPassed, setShowPassed] = useState(false);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sending, setSending] = useState(false);
  // Only decorates the path and the to-do row with who has an open request. A read that fails (a role
  // the server does not answer for) draws nothing extra and is never read as "no request": the
  // approval check beside it is the server's own and still says what is outstanding.
  const request = work.data?.approval;
  const reviewer = useReviewerWords(
    request && isOpenRequest(request)
      ? request.stages.filter((stage) => stage.position === request.current_position).map((stage) => stage.reviewer_id)
      : [],
  );

  const goView = (tab: string) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, filing: active.obligation_id, tab }),
    });

  if (checks.error) {
    return (
      <TablePage
        icon={Flag}
        title={title}
        label="Filing"
        extra={lead}
        state={
          <ReadFailed
            of="This filing's checks"
            error={checks.error}
            onRetry={() => void checks.refetch()}
          />
        }
      />
    );
  }
  if (!checks.data) {
    return (
      <TablePage icon={Flag} title={title} label="Filing" extra={lead} state={<EmptyState title="Reading this filing" compact />} />
    );
  }

  const read = checks.data;
  const { summary } = read;
  const requested = request && isOpenRequest(request) ? reviewer : undefined;
  const built = buildTodo(read);
  // A request that is open has been sent: the row says who has it rather than offering to send again.
  const todo = { open: withOpenRequest(built.open, requested), passed: built.passed };
  const rows = (showPassed ? [...todo.open, ...todo.passed] : todo.open).filter((row) => matches(row, filters));
  // The chip says the filing's state once something has been issued, and the checks' verdict before.
  const verdict = headlineWord(summary.verdict, read.filing_state);
  const blocker = blockerText(todo.open);
  const first = todo.open[0];
  const latestIssued = artifacts.data?.[0];
  const issuedCount = artifacts.data ? String(artifacts.data.length) : "–";
  const stationOptions = read.parts.map((part) => ({ value: part.part, label: part.label }));
  const steps = withReviewer(stepsFromChecks(read.parts, summary.verdict, read.filing_state), requested);
  const sendTitle = `Send ${title.split(",")[0] ?? "this filing"} for approval`;
  const openSend = () => setSending(true);
  // A filing that is already issued has nothing left to be late for; any other is dated by `dueOf`.
  const due = dueOf(active, latestIssued ? "issued" : "blocked", isoToday(new Date()));
  const dueHasDate = Boolean(active.due_date);

  const blockerLink =
    first?.fix?.permitted === true ? (
      <FixCell row={first} filing={active.obligation_id} onSend={openSend} />
    ) : undefined;

  const hero = (
    <>
      {lead}
      <section
        className={`card ${styles.band}`}
        aria-label="Readiness"
        data-testid="filing-band"
        data-verdict={summary.verdict}
      >
        <div className={styles.ready}>
          <div className={styles.heroLeft}>
            <div className={styles.big}>
              <div className={styles.percent} data-testid="readiness-percent" data-value={summary.readiness_percent ?? ""}>
                {percentText(summary.readiness_percent)}
              </div>
              <span className={styles.percentLabel}>ready</span>
              <InfoPopover infoKey="filings.readiness" />
              {/* The year end sat beside the title and wrapped it onto four lines at 1024 (design
                  review 2026-11-03); it is a fact about the filing, so it sits with the figure. */}
              <StatusChip tone="neutral" size="sm" testId="year-end-chip">
                Year ended {shortDate(active.financial_year_end)}
              </StatusChip>
            </div>
            <div className={styles.verdict}>
              <StatusChip tone={verdict.tone} testId="verdict-word">
                {verdict.word}
              </StatusChip>
              <ServerNote
                label={supportLevelWords(read.support_level)}
                exact={read.support_level}
                title="Support level"
                text={read.support_because}
                testId="support-chip"
              />
              {read.in_force_label ? (
                <ServerNote
                  label={customerText(read.in_force_label)}
                  title={customerText(read.in_force_label)}
                  text={read.in_force_caveat}
                  tone="warn"
                  testId="in-force-chip"
                />
              ) : null}
            </div>
          </div>
          <TileStrip label="This filing">
            <Tile
              label="Due"
              value={active.due_date ? shortDate(active.due_date) : "No date set"}
              testId="tile-due"
              link={
                dueHasDate ? (
                  due.words && due.words !== "Issued" ? (
                    <StatusChip tone={due.tone} size="sm" testId="due-chip">
                      {due.words}
                    </StatusChip>
                  ) : undefined
                ) : (
                  <ServerNote
                    label="Why"
                    title="No date set"
                    text={active.due_date_basis}
                    testId="due-why"
                  />
                )
              }
            />
            <Tile label="Checks" value={`${String(summary.passed)} of ${String(summary.total)}`} unit="passed" testId="tile-passed" />
            {/* Versions, not filings: this page is one filing, and each issue of it is a version (a
                count of "2 filings issued" on one filing read as two filings, G-Wauditor-03). */}
            <Tile label="Versions issued" value={issuedCount} testId="tile-issued" />
          </TileStrip>
        </div>
        <PathToFiling
          steps={steps}
          {...(blocker ? { blocker: { text: blocker, ...(blockerLink ? { link: blockerLink } : {}) } } : {})}
        />
      </section>
    </>
  );

  return (
    <TablePage
      icon={Flag}
      title={title}
      actions={
        <FilingActions
          obligationId={active.obligation_id}
          method={method}
          role={me?.role}
          latestIssued={latestIssued}
          onSend={openSend}
        />
      }
      extra={hero}
      tableFitsRows
      filters={
        <FilterLine
          filters={[{ id: "station", label: "Station", options: stationOptions }]}
          state={filters}
          onChange={setFilters}
          searchLabel="Search the to-do list"
          resultCount={{ shown: rows.length, total: showPassed ? todo.open.length + todo.passed.length : todo.open.length, noun: "items" }}
          actions={
            <>
              <label>
                <input
                  type="checkbox"
                  checked={showPassed}
                  onChange={(event) => setShowPassed(event.target.checked)}
                  data-testid="show-passed"
                />{" "}
                Show passed
              </label>
              <InfoPopover infoKey="filings.todo" />
            </>
          }
        />
      }
      table={
        <GridTable
          caption="To-do list"
          columns={columnsFor(active.obligation_id, openSend)}
          rows={rows}
          rowKey={(row) => row.id}
          rowNumbers={false}
          zebra
          testId="todo-table"
          empty={
            <EmptyState
              compact
              title={
                summary.total === 0
                  ? "Nothing was checked"
                  : todo.open.length === 0
                    ? "Nothing left to do"
                    : "Nothing matches"
              }
              testId="todo-empty"
            />
          }
        />
      }
      after={
        <>
          {report.error ? (
            <section className="card" aria-label="What this filing states">
              <ReadFailed
                of="The figures this filing states"
                error={report.error}
                onRetry={() => void report.refetch()}
              />
            </section>
          ) : report.data ? (
            <StatedBand report={report.data} obligationId={active.obligation_id} />
          ) : null}
          <FilingFacts
            active={active}
            method={method}
            canDraft={me?.role !== undefined && MAY_DRAFT.has(me.role)}
            onOpenPack={() => goView("submission")}
          />
          {/* In its own block: beside the law's tab strip above the hero it would be a second row of one
              repeated structure, which the alignment gate (rightly) measures as a ragged one. */}
          <div className={styles.views}>
            <SectionTabs
              label="Filing details"
              testId="filing-details"
              tabs={DETAIL_VIEWS.map((view) => ({ key: view.tab, label: view.label, current: false }))}
              onSelect={goView}
            />
          </div>
          <SendForApprovalDrawer
            open={sending}
            onClose={() => setSending(false)}
            obligationId={active.obligation_id}
            title={sendTitle}
          />
        </>
      }
    />
  );
}
